import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FindAllSettingsCatalogPayload,
  SettingPayload,
  SettingsCatalogService,
} from "../services/settings-catalog.service";

const SETTINGS_CATALOG_LIST_KEY = "pret-settings-catalog-list";
const SETTING_BY_ID_KEY = "pret-setting-by-id";

export const useSettingsCatalog = (filters: FindAllSettingsCatalogPayload) => {
  const {
    data: settings,
    isLoading: isLoadingSettings,
    isFetching: isFetchingSettings,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: [SETTINGS_CATALOG_LIST_KEY, filters],
    queryFn: () => SettingsCatalogService.findAll(filters),
  });

  return {
    settings,
    isLoadingSettings,
    isFetchingSettings,
    refetchSettings,
  };
};

export const useSetting = (id: string, enabled = true) => {
  const {
    data: setting,
    isLoading: isLoadingSetting,
    refetch: refetchSetting,
  } = useQuery({
    queryKey: [SETTING_BY_ID_KEY, id],
    queryFn: () => SettingsCatalogService.findOne(id),
    enabled: !!id && enabled,
  });

  return {
    setting,
    isLoadingSetting,
    refetchSetting,
  };
};

export const useCreateSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SettingPayload) => SettingsCatalogService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SETTINGS_CATALOG_LIST_KEY] });
    },
  });
};

export const useUpdateSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SettingPayload }) =>
      SettingsCatalogService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [SETTINGS_CATALOG_LIST_KEY] });
      queryClient.invalidateQueries({
        queryKey: [SETTING_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => SettingsCatalogService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SETTINGS_CATALOG_LIST_KEY] });
    },
  });
};
